import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, LinearProgress, MenuItem, MenuList, Typography } from '@material-ui/core';
import { kebabCase } from 'lodash';
import React, { useRef, useState } from 'react';
import { translate } from '../../../../../common/intl';
import HeaderLastSaved from '../../../../components/common/header/HeaderLastSaved';
import { Button } from '../../../../components/forms/fields-next';
import { withThemeNext } from '../../../../layout/theme-next';
import SettingsHistoryDrawer from '../../../general.settings/components/SettingsPage/SettingsHistoryDrawer';
import { COMPANY_INFORMATION_AUDIT_PARENT_KEYS, panels } from '../../constants';
import { CompanyInformationProps, CompanyInformationTabs } from '../../types';
import useStyles from './styles';

const CompanyInformationPage: React.FC<CompanyInformationProps> = ({
  updatedAt,
  isLoading,
}) => {
  const classes = useStyles();

  const [expandedPanels, setExpanedPanels] = useState<CompanyInformationTabs[]>([]);
  const allPanelsExpaned = expandedPanels.length === panels.length;

  const containerRef = useRef<HTMLDivElement>(null);

  const toggleAllPanels = () => setExpanedPanels(allPanelsExpaned ? [] : panels.map(panel => panel.key));
  const togglePanel = (key: CompanyInformationTabs) => () => setExpanedPanels(state => (
    state.includes(key) ? state.filter(it => it !== key) : [...state, key]
  ));
  const jumpToPanel = (key: CompanyInformationTabs) => () => setExpanedPanels([key]);

  const [isShowHistoryDrawer, setIsShowHistoryDrawer] = useState<boolean>(false);
  const showHistoryDialog = () => setIsShowHistoryDrawer(true);
  const handleHistoryDrawerClose = () => setIsShowHistoryDrawer(false);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography
            variant="h1"
            className={classes.title}
          >
            {translate('administration.company.information.title')}
          </Typography>
          <Typography
            variant="subtitle1"
            className={classes.subtitle}
          >
            {translate('administration.company.information.subtitle')}
          </Typography>
          <br className={classes.spaceTitle} />
        </Box>
        <Box className={classes.actions}>
          {updatedAt && (
            <HeaderLastSaved
              timestamp={updatedAt}
              loading={isLoading}
              className={classes.lastSaved}
              icon={<FontAwesomeIcon icon={solid('cloud-check')} className={classes.lastSavedIcon} />}
            />
          )}
          <Box className={classes.buttons}>
            <Button onClick={showHistoryDialog} kind="outlined" data-cy="settings-history">{translate('common.history')}</Button>
          </Box>
        </Box>
        {isLoading && <LinearProgress className={classes.progress} data-cy="saving-progress-bar" />}
      </Box>
      <Box className={classes.sidebar}>
        <MenuList className={classes.list}>
          {panels.map(panel => (
            <MenuItem
              key={panel.key}
              className={classes.listItem}
              onClick={jumpToPanel(panel.key)}
              selected={expandedPanels.includes(panel.key)}
              data-cy={`link-${kebabCase(panel.title)}`}
            >
              {translate(panel.title)}
            </MenuItem>
          ))}
        </MenuList>
      </Box>
      <Box className={classes.toggler}>
        <Button kind="ghost" onClick={toggleAllPanels}>
          {translate(allPanelsExpaned ? 'common.collapse.all' : 'common.expand.all')}
        </Button>
      </Box>
      <div className={classes.panels} ref={containerRef}>
        {panels.map(panel => (
          <panel.component
            key={panel.key}
            title={panel.title}
            containerRef={containerRef}
            expanded={expandedPanels.includes(panel.key)}
            onToggle={togglePanel(panel.key)}
            data-cy={kebabCase(panel.title)}
          />
        ))}
      </div>
      {isShowHistoryDrawer && (
        <SettingsHistoryDrawer
          isOpen={isShowHistoryDrawer}
          onClose={handleHistoryDrawerClose}
          parentKeys={COMPANY_INFORMATION_AUDIT_PARENT_KEYS}
        />
      )}
    </Box>
  );
};

export default withThemeNext(CompanyInformationPage);
